<template>
  <section id="boxscore" class="boxscore-section" aria-label="Traditional Boxscore">
    <div class="boxscore-section__inner">
      <div class="section__header">
        <div class="section-number">I</div>
        <div class="section__titles">
          <p class="eyebrow">The Starting Point &mdash; Line Score</p>
          <h2 class="display-lg">Here's the <em>game</em>.</h2>
          <p class="body-lg section__dek">
            A traditional box score &mdash; the familiar view. But underneath, thousands of
            simulations are already asking: <em>what else could happen?</em>
          </p>
        </div>
      </div>

      <hr class="rule rule--double" />

      <div class="scorecard-grid">
        <!-- Batting Table -->
        <article class="scorecard">
          <header class="scorecard__header">
            <span class="scorecard__n numeric">№ 01</span>
            <h3 class="scorecard__title">Batting</h3>
            <span class="scorecard__tag">AB &middot; R &middot; H &middot; HR &middot; RBI</span>
          </header>
          <div class="scorecard__scroll">
            <table class="scorecard__table scorecard__table--batting">
              <thead>
                <tr>
                  <th scope="col" class="th--player">Player</th>
                  <th scope="col" class="th--pos">Pos</th>
                  <th scope="col" class="th--num">AB</th>
                  <th scope="col" class="th--num">R</th>
                  <th scope="col" class="th--num">H</th>
                  <th scope="col" class="th--num">HR</th>
                  <th scope="col" class="th--num">RBI</th>
                </tr>
              </thead>
              <template v-for="group in battingGroups" :key="`bat-grp-${group.team}`">
                <tbody>
                  <tr class="row row--team-banner">
                    <th colspan="7" scope="rowgroup" class="team-banner">
                      <span class="team-banner__tag">{{ teamAbbrFor(group.team) }}</span>
                      <span class="team-banner__label">{{ teamLabelFor(group.team) }}</span>
                    </th>
                  </tr>
                  <tr v-for="player in group.players" :key="`bat-${player.player_id}`" class="row">
                    <td class="td--player">
                      <span class="player-name">{{ player.name }}</span>
                    </td>
                    <td class="td--pos">{{ player.position }}</td>
                    <td class="td--num numeric">{{ player.ab }}</td>
                    <td class="td--num numeric">{{ player.r }}</td>
                    <td class="td--num numeric">{{ player.h }}</td>
                    <td class="td--num numeric">{{ player.hr }}</td>
                    <td class="td--num numeric">{{ player.rbi }}</td>
                  </tr>
                  <tr class="row row--totals">
                    <td class="td--player">{{ teamAbbrFor(group.team) }} Totals</td>
                    <td class="td--pos">&mdash;</td>
                    <td class="td--num numeric">{{ group.totals.ab }}</td>
                    <td class="td--num numeric">{{ group.totals.r }}</td>
                    <td class="td--num numeric">{{ group.totals.h }}</td>
                    <td class="td--num numeric">{{ group.totals.hr }}</td>
                    <td class="td--num numeric">{{ group.totals.rbi }}</td>
                  </tr>
                </tbody>
              </template>
            </table>
          </div>
        </article>

        <!-- Pitching Table -->
        <article class="scorecard">
          <header class="scorecard__header">
            <span class="scorecard__n numeric">№ 02</span>
            <h3 class="scorecard__title">Pitching</h3>
            <span class="scorecard__tag"
              >IP &middot; H &middot; R &middot; ER &middot; BB &middot; K</span
            >
          </header>
          <div class="scorecard__scroll">
            <table class="scorecard__table scorecard__table--pitching">
              <thead>
                <tr>
                  <th scope="col" class="th--player">Pitcher</th>
                  <th scope="col" class="th--num">IP</th>
                  <th scope="col" class="th--num">H</th>
                  <th scope="col" class="th--num">R</th>
                  <th scope="col" class="th--num">ER</th>
                  <th scope="col" class="th--num">BB</th>
                  <th scope="col" class="th--num">K</th>
                </tr>
              </thead>
              <template v-for="group in pitchingGroups" :key="`pit-grp-${group.team}`">
                <tbody>
                  <tr class="row row--team-banner">
                    <th colspan="7" scope="rowgroup" class="team-banner">
                      <span class="team-banner__tag">{{ teamAbbrFor(group.team) }}</span>
                      <span class="team-banner__label">{{ teamLabelFor(group.team) }}</span>
                    </th>
                  </tr>
                  <tr
                    v-for="pitcher in group.pitchers"
                    :key="`pit-${pitcher.player_id}`"
                    class="row"
                  >
                    <td class="td--player">
                      <span class="player-name">{{ pitcher.name }}</span>
                    </td>
                    <td class="td--num numeric">{{ pitcher.ip.toFixed(1) }}</td>
                    <td class="td--num numeric">{{ pitcher.h }}</td>
                    <td class="td--num numeric">{{ pitcher.r }}</td>
                    <td class="td--num numeric">{{ pitcher.er }}</td>
                    <td class="td--num numeric">{{ pitcher.bb }}</td>
                    <td class="td--num numeric">{{ pitcher.k }}</td>
                  </tr>
                  <tr class="row row--totals">
                    <td class="td--player">{{ teamAbbrFor(group.team) }} Totals</td>
                    <td class="td--num numeric">{{ group.totals.ip.toFixed(1) }}</td>
                    <td class="td--num numeric">{{ group.totals.h }}</td>
                    <td class="td--num numeric">{{ group.totals.r }}</td>
                    <td class="td--num numeric">{{ group.totals.er }}</td>
                    <td class="td--num numeric">{{ group.totals.bb }}</td>
                    <td class="td--num numeric">{{ group.totals.k }}</td>
                  </tr>
                </tbody>
              </template>
            </table>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Batter {
  player_id: string
  name: string
  team: string
  position: string
  ab: number
  r: number
  h: number
  hr: number
  rbi: number
}

interface Pitcher {
  player_id: string
  name: string
  team: string
  ip: number
  h: number
  r: number
  er: number
  bb: number
  k: number
}

interface BattingGroup {
  team: string
  players: Batter[]
  totals: { ab: number; r: number; h: number; hr: number; rbi: number }
}

interface PitchingGroup {
  team: string
  pitchers: Pitcher[]
  totals: { ip: number; h: number; r: number; er: number; bb: number; k: number }
}

const props = defineProps<{
  batting: Batter[]
  pitching: Pitcher[]
  homeAbbr: string
  awayAbbr: string
}>()

function teamAbbrFor(team: string): string {
  return team === 'home' ? props.homeAbbr : props.awayAbbr
}

function teamLabelFor(team: string): string {
  return team === 'home' ? 'Home' : 'Away'
}

// Render the visiting team first (traditional boxscore convention),
// then the home team.
const TEAM_ORDER: readonly string[] = ['away', 'home'] as const

const battingGroups = computed((): BattingGroup[] => {
  return TEAM_ORDER.map(team => {
    const players = props.batting.filter(p => p.team === team)
    if (players.length === 0) return null
    const totals = players.reduce(
      (acc, p) => ({
        ab: acc.ab + p.ab,
        r: acc.r + p.r,
        h: acc.h + p.h,
        hr: acc.hr + p.hr,
        rbi: acc.rbi + p.rbi
      }),
      { ab: 0, r: 0, h: 0, hr: 0, rbi: 0 }
    )
    return { team, players, totals }
  }).filter((g): g is BattingGroup => g !== null)
})

const pitchingGroups = computed((): PitchingGroup[] => {
  return TEAM_ORDER.map(team => {
    const pitchers = props.pitching.filter(p => p.team === team)
    if (pitchers.length === 0) return null
    const totals = pitchers.reduce(
      (acc, p) => ({
        ip: acc.ip + p.ip,
        h: acc.h + p.h,
        r: acc.r + p.r,
        er: acc.er + p.er,
        bb: acc.bb + p.bb,
        k: acc.k + p.k
      }),
      { ip: 0, h: 0, r: 0, er: 0, bb: 0, k: 0 }
    )
    return { team, pitchers, totals }
  }).filter((g): g is PitchingGroup => g !== null)
})
</script>

<style scoped>
.boxscore-section {
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem;
}

/* ─── Section header (matches LandingView) ──────────────────────── */
.section__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  max-width: 960px;
}

.section__titles .eyebrow {
  margin-bottom: 1rem;
}

.section__titles .display-lg em {
  font-style: italic;
  color: var(--ox);
}

.section__dek {
  margin-top: 1.25rem;
  max-width: 58ch;
}

.section__dek em {
  font-style: italic;
  color: var(--ox);
}

/* ─── Scorecard layout ──────────────────────────────────────────── */
.scorecard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.scorecard {
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
  box-shadow: var(--shadow-press);
  position: relative;
}

.scorecard__header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: baseline;
  gap: 0.75rem;
  padding: 1rem 1.5rem 0.85rem;
  border-bottom: var(--rule-thin) solid var(--ink);
}

.scorecard__n {
  font-size: 0.7rem;
  color: var(--ink-ghost);
}

.scorecard__title {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 700;
  font-size: 1.5rem;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.scorecard__tag {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ox);
}

/* Horizontal scroll wrapper — keeps wide tables from breaking mobile layout */
.scorecard__scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.scorecard__table {
  width: 100%;
  min-width: 420px;
  border-collapse: collapse;
  background: var(--paper);
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  color: var(--ink);
}

.scorecard__table thead {
  background: var(--paper-deep);
  border-bottom: var(--rule-med) solid var(--ink);
}

.scorecard__table th {
  padding: 0.6rem 0.65rem;
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-medium);
  text-align: left;
  white-space: nowrap;
}

.scorecard__table th.th--num {
  text-align: right;
}

.scorecard__table td {
  padding: 0.55rem 0.65rem;
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
  vertical-align: baseline;
  line-height: 1.35;
}

/* Remove the dashed border on the last row of each team group. */
.scorecard__table tbody tr:last-child td {
  border-bottom: none;
}

/* ─── Team banner row (section header for Away / Home) ─────────── */
.row--team-banner .team-banner {
  padding: 0.55rem 0.65rem;
  background: var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  text-align: left;
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

.team-banner__tag {
  color: var(--paper);
  margin-right: 0.65rem;
  padding: 0.15rem 0.45rem;
  background: var(--ox);
  font-family: var(--font-data);
  font-size: 0.66rem;
  letter-spacing: 0.08em;
}

.team-banner__label {
  color: var(--paper);
  font-size: 0.6rem;
  letter-spacing: 0.24em;
  opacity: 0.75;
}

/* ─── Data rows ─────────────────────────────────────────────────── */
.row:hover td {
  background: var(--paper-deep);
}

.td--player {
  white-space: nowrap;
}

.player-name {
  font-family: var(--font-editorial);
  font-weight: 600;
  color: var(--ink);
}

.td--pos {
  font-family: var(--font-ui);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-faint);
  white-space: nowrap;
}

.td--num {
  text-align: right;
  font-size: 0.95rem;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

/* ─── Team totals row ───────────────────────────────────────────── */
.row--totals td {
  border-top: var(--rule-thin) solid var(--ink);
  border-bottom: none !important;
  padding-top: 0.65rem;
  padding-bottom: 0.75rem;
  background: var(--ox-10);
  font-weight: 700;
  color: var(--ox);
}

.row--totals .td--player {
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ox);
}

.row--totals:hover td {
  background: var(--ox-10);
}

/* Separate the two team groups with a bit of breathing room. */
.scorecard__table tbody + tbody .row--team-banner .team-banner {
  border-top: var(--rule-med) solid var(--ink);
}

/* ─── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .scorecard-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

@media (max-width: 700px) {
  .boxscore-section {
    padding: 3rem 1rem;
  }

  .section__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .scorecard__header {
    padding: 0.85rem 1rem 0.75rem;
    grid-template-columns: auto 1fr;
  }

  .scorecard__tag {
    display: none;
  }

  .scorecard__table {
    font-size: 0.88rem;
  }

  .scorecard__table th,
  .scorecard__table td {
    padding: 0.5rem 0.5rem;
  }
}
</style>
